import { Tabs } from '@/components/ui/Tabs';
import { useAuth } from '@/features/auth/useAuth';
import type { UserRole } from '@coreltb/shared/constants';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Zakładki sekcji zamiast osobnych pozycji w menu (Dawid 2026-10-01: „za dużo
 * rzeczy w menu admina”). Trasa zgłasza grupę przez `handle.tabs`, a układ
 * rysuje zakładki nad stroną. Zakładki spoza roli są ukryte; jedna = brak paska.
 */
export type SectionTabsGroup = 'settings' | 'reports' | 'tools';

interface SectionTab {
  to: string;
  label: string;
  visibleFor: readonly UserRole[];
}

const GROUPS: Record<SectionTabsGroup, readonly SectionTab[]> = {
  settings: [
    { to: '/ustawienia', label: 'Ogólne', visibleFor: ['admin'] },
    { to: '/ustawienia/cennik', label: 'Cennik ofert', visibleFor: ['admin'] },
    { to: '/ustawienia/szablony', label: 'Szablony umów', visibleFor: ['admin'] },
  ],
  reports: [
    { to: '/raporty', label: 'Raporty', visibleFor: ['admin', 'dyrektor_sprzedazy'] },
    { to: '/analiza-leadow', label: 'Analiza leadów', visibleFor: ['admin'] },
  ],
  tools: [
    { to: '/ustawienia/reminders-audit', label: 'Audyt', visibleFor: ['developer'] },
    { to: '/ustawienia/uzycie', label: 'Użycie Cloudflare', visibleFor: ['developer'] },
    { to: '/ustawienia/import', label: 'Import ze starego systemu', visibleFor: ['developer'] },
  ],
};

/** Ścieżki grupy — menu podświetla jedną pozycję dla wszystkich zakładek sekcji. */
export function sectionPaths(group: SectionTabsGroup): string[] {
  return GROUPS[group].map((t) => t.to);
}

function activeTab(tabs: readonly SectionTab[], pathname: string): SectionTab | undefined {
  return [...tabs]
    .sort((a, b) => b.to.length - a.to.length)
    .find((t) => pathname === t.to || pathname.startsWith(`${t.to}/`));
}

export function SectionTabs({ group }: { group: SectionTabsGroup }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const role = user?.role;
  const tabs = GROUPS[group].filter(
    (t) => role !== undefined && (role === 'developer' || t.visibleFor.includes(role)),
  );
  const first = tabs[0];
  if (!first || tabs.length < 2) return null;

  return (
    <Tabs
      ariaLabel="Zakładki sekcji"
      className="mb-5 md:mb-6"
      value={(activeTab(tabs, pathname) ?? first).to}
      onChange={(to) => navigate(to)}
      items={tabs.map((t) => ({ value: t.to, label: t.label }))}
    />
  );
}
